// src/api/workflowAPI.js

const normalizeBaseUrl = (baseUrl) => String(baseUrl || "").replace(/\/+$/, "");

const resolveApiBaseUrl = () => {
  const envBaseUrl = normalizeBaseUrl(process.env.VUE_APP_API_BASE_URL);
  if (envBaseUrl && envBaseUrl !== "undefined" && envBaseUrl !== "null") {
    return envBaseUrl;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return normalizeBaseUrl(`${window.location.origin}/assemble/assembleLog`);
  }

  return "/assemble/assembleLog";
};

// 后端API的基础URL，优先使用环境变量，缺失时回退到同域部署路径
const API_BASE_URL = resolveApiBaseUrl();
const AUTH_TOKEN =
  "sk_live_5A$b8X@k3P#m6R9tQ2vS7uY4wZ1cD0eF5gH6jK7lM8nO9pQ0rS2tU4vW6xY8z";

const buildHeaders = (headers = {}) => ({
  "X-Auth-Token": AUTH_TOKEN,
  ...headers,
});

const buildBatchVerifyFilename = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `批量核对${yyyy}${mm}${dd}.xlsx`;
};

const buildBatchVerifyResultFilename = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `批量核对结果${yyyy}${mm}${dd}.xlsx`;
};

const buildDataCheckResultFilename = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `数据核对结果${yyyy}${mm}${dd}.xlsx`;
};

const buildDataCheckTemplateFilename = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `数据核对模板${yyyy}${mm}${dd}.xlsx`;
};

const resolveFilename = (contentDisposition, fallbackFilename) => {
  const filenameMatch =
    contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
    contentDisposition.match(/filename="?([^"]+)"?/i);
  const rawFilename = filenameMatch ? filenameMatch[1] : "";

  if (
    !rawFilename ||
    rawFilename === "download" ||
    rawFilename === "download.xlsx"
  ) {
    return fallbackFilename;
  }

  try {
    return decodeURIComponent(rawFilename);
  } catch (error) {
    return rawFilename || fallbackFilename;
  }
};

/**
 * 通用的API调用函数
 * @param {string} endpoint - 请求的API端点 (例如 'validate-shell')
 * @param {object} payload - 发送到后端的数据
 * @returns {Promise<{success: boolean, data: any, message: string}>}
 */
const apiCall = async (endpoint, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: "POST",
      headers: {
        ...buildHeaders({
          "Content-Type": "application/json",
        }), // 添加鉴权Token
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json(); // 后端返回 AjaxResult 格式
    if (!response.ok || result.code !== 200) {
      console.error(
        `API Error on ${endpoint}:`,
        result.msg || response.statusText
      );
      // 返回一个标准的失败结构，其中 message 来自后端
      return { success: false, data: null, message: result.msg || "请求失败" };
    }

    // 返回一个标准的成功结构
    return { success: true, data: result.data, message: result.msg };
  } catch (error) {
    console.error(`Network Error on ${endpoint}:`, error);
    // 网络层面的错误
    return {
      success: false,
      data: null,
      message: "网络连接失败，请检查网络后重试",
    };
  }
};

const downloadFile = async (
  endpoint,
  formData,
  fallbackFilename = buildBatchVerifyFilename()
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: "POST",
      headers: buildHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        message: errorText || "请求失败",
      };
    }

    const blob = await response.blob();
    const contentDisposition =
      response.headers.get("content-disposition") || "";
    const filename = resolveFilename(contentDisposition, fallbackFilename);

    return {
      success: true,
      data: {
        blob,
        filename,
      },
      message: "成功",
    };
  } catch (error) {
    console.error(`Network Error on ${endpoint}:`, error);
    return {
      success: false,
      message: "网络连接失败，请检查网络后重试",
    };
  }
};

const downloadFileByGet = async (
  endpoint,
  fallbackFilename = buildBatchVerifyFilename()
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: "GET",
      headers: buildHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        message: errorText || "请求失败",
      };
    }

    const blob = await response.blob();
    const contentDisposition =
      response.headers.get("content-disposition") || "";
    const filename = resolveFilename(contentDisposition, fallbackFilename);

    return {
      success: true,
      data: {
        blob,
        filename,
      },
      message: "成功",
    };
  } catch (error) {
    console.error(`Network Error on ${endpoint}:`, error);
    return {
      success: false,
      message: "网络连接失败，请检查网络后重试",
    };
  }
};

// --- 灌胶流程相关API ---
export const pottingAPI = {
  /**
   * 校验外壳二维码
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateDeviceByShell: (data) => apiCall("validate-shell", data),
  /**
   * 校验板子二维码
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateDeviceByBoard: (data) => apiCall("validate-board", data),
  /**
   * 保存灌胶流程最终状态
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  saveState: (data) => apiCall("save", data),
};

// --- 焊接流程相关API ---
export const weldingAPI = {
  /**
   * 校验外壳二维码
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateDevice: (data) => apiCall("validate-shell", data),
  /**
   * 检查设备上电后的状态
   * @param {object} data - { deviceId, operation, seriesId, content: null }
   */
  checkUploadStatus: (data) => apiCall("systemCheck", data),
  /**
   * 保存焊接流程最终状态
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  saveState: (data) => apiCall("save", data),
};

// --- 绑定流程相关API ---
export const bindingAPI = {
  /**
   * 校验外壳二维码
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateShellCode: (data) => apiCall("validate-shell", data),
  /**
   * 校验左侧标签
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateLeftTab: (data) => apiCall("scanLeftTab", data),
  /**
   * 校验右侧标签
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateRightTab: (data) => apiCall("scanRightTab", data),
  /**
   * 保存绑定流程最终状态
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  saveState: (data) => apiCall("save", data),
};

// --- 最终检查流程相关API ---
export const finalCheckAPI = {
  /**
   * 校验外壳二维码
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateDevice: (data) => apiCall("check/validate-device", data),
  /**
   * 检查设备上电后的状态
   * @param {object} data - { deviceId, operation, seriesId, content: null }
   */
  finalCheck: (data) => apiCall("check/final", data),
  /**
   * 保存流程最终状态
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  saveState: (data) => apiCall("check/save", data),
};

// --- 内部抽检流程相关API ---
export const innerSamplingAPI = {
  /**
   * 校验外壳二维码
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  validateDevice: (data) => apiCall("check/validate-device", data),
  /**
   * 校验左侧标签
   * @param {object} data - { deviceId, operation, seriesId, content: null }
   */
  validateLeftTab: (data) => apiCall("check/validate-left-tab", data),
  /**
   * 校验右侧标签
   * @param {object} data - { deviceId, operation, seriesId, content: null }
   */
  validateRightTab: (data) => apiCall("check/validate-right-tab", data),
  /**
   * 保存流程最终状态
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  saveState: (data) => apiCall("check/save", data),
};

export const batchVerifyAPI = {
  verifyTabs: (formData) =>
    downloadFile(
      "check/batch-verify-tabs",
      formData,
      buildBatchVerifyResultFilename()
    ),
  downloadTemplate: () =>
    downloadFileByGet(
      "check/batch-verify-tabs/template",
      buildBatchVerifyFilename()
    ),
};

export const dataCheckAPI = {
  compareFiles: (formData) =>
    downloadFile(
      "check/compare-tab-files",
      formData,
      buildDataCheckResultFilename()
    ),
  downloadTemplate: () =>
    downloadFileByGet(
      "check/compare-tab-files/template",
      buildDataCheckTemplateFilename()
    ),
};

// --- 设备状态流程相关API ---
export const checkStatusAPI = {
  /**
   * 获取设备状态
   * @param {object} data - { deviceId, operation, seriesId, content }
   */
  checkStatus: (data) => apiCall("check/status", data),
};

// --- 通用异常上报API ---
/**
 * 上报异常信息
 * @param {object} errorInfo - { deviceId, operation, seriesId, extraData }
 */
export const reportExceptionAPI = (errorInfo) =>
  apiCall("uploadError", errorInfo);
